export const IDX_LAUNCHLAB_CREATE_DRAFT_KEY = "idx-launchlab-create-draft";
export const IDX_LAUNCHLAB_CREATE_DRAFT_LOGO_KEY = "idx-launchlab-create-draft-logo";
export const IDX_LAUNCHLAB_CREATE_DRAFT_BANNER_KEY = "idx-launchlab-create-draft-banner";

/** Skip drafts larger than ~4MB — oversized JSON can hang or break JSON.parse. */
const MAX_DRAFT_BYTES = 4_000_000;

function storageGet(storage, key) {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function storageRemove(storage, key) {
  try {
    storage.removeItem(key);
  } catch {
    /* ignore */
  }
}

function storageSet(storage, key, value) {
  try {
    if (!value) {
      storageRemove(storage, key);
      return true;
    }
    storage.setItem(key, value);
    return true;
  } catch (error) {
    console.warn("Launchlab create draft could not be saved:", error);
    return false;
  }
}

/**
 * Move logo/banner data URLs out of the JSON blob so a large banner cannot
 * drop the rest of the draft (name, symbol, socials, logo).
 * @param {unknown} persistValue
 */
export function detachDraftImages(persistValue) {
  const token = persistValue?.state?.newToken;
  const logo = typeof token?.previewLogoDataUrl === "string" ? token.previewLogoDataUrl : "";
  const banner = typeof token?.previewBannerDataUrl === "string" ? token.previewBannerDataUrl : "";
  if (!token) {
    return { persistValue, logo, banner };
  }
  return {
    persistValue: {
      ...persistValue,
      state: {
        ...persistValue.state,
        newToken: {
          ...token,
          previewLogoDataUrl: undefined,
          previewBannerDataUrl: undefined,
        },
      },
    },
    logo,
    banner,
  };
}

/**
 * @param {unknown} persistValue
 * @param {string | null} logo
 * @param {string | null} banner
 */
export function attachDraftImages(persistValue, logo, banner) {
  const token = persistValue?.state?.newToken;
  if (!token) return persistValue;
  return {
    ...persistValue,
    state: {
      ...persistValue.state,
      newToken: {
        ...token,
        ...(logo ? { previewLogoDataUrl: logo } : {}),
        ...(banner ? { previewBannerDataUrl: banner } : {}),
      },
    },
  };
}

/**
 * @param {Pick<Storage, "getItem" | "setItem" | "removeItem">} storage
 */
export function createLaunchlabDraftStorage(storage) {
  return {
    getItem: (name) => {
      try {
        const raw = storageGet(storage, name);
        if (!raw) return null;
        if (raw.length > MAX_DRAFT_BYTES) {
          storageRemove(storage, name);
          console.warn("Launchlab create draft was too large and was cleared.");
          return null;
        }
        const parsed = JSON.parse(raw);
        const logo = storageGet(storage, IDX_LAUNCHLAB_CREATE_DRAFT_LOGO_KEY);
        const banner = storageGet(storage, IDX_LAUNCHLAB_CREATE_DRAFT_BANNER_KEY);
        return attachDraftImages(parsed, logo, banner);
      } catch {
        storageRemove(storage, name);
        storageRemove(storage, IDX_LAUNCHLAB_CREATE_DRAFT_LOGO_KEY);
        storageRemove(storage, IDX_LAUNCHLAB_CREATE_DRAFT_BANNER_KEY);
        console.warn("Launchlab create draft was invalid and was cleared.");
        return null;
      }
    },
    setItem: (name, value) => {
      const { persistValue, logo, banner } = detachDraftImages(value);
      let text;
      try {
        text = JSON.stringify(persistValue);
      } catch (error) {
        console.warn("Launchlab create draft could not be saved:", error);
        return;
      }
      if (text.length > MAX_DRAFT_BYTES) {
        console.warn("Launchlab create draft was too large and was not saved.");
        return;
      }
      storageSet(storage, name, text);
      storageSet(storage, IDX_LAUNCHLAB_CREATE_DRAFT_LOGO_KEY, logo);
      storageSet(storage, IDX_LAUNCHLAB_CREATE_DRAFT_BANNER_KEY, banner);
    },
    removeItem: (name) => {
      storageRemove(storage, name);
      storageRemove(storage, IDX_LAUNCHLAB_CREATE_DRAFT_LOGO_KEY);
      storageRemove(storage, IDX_LAUNCHLAB_CREATE_DRAFT_BANNER_KEY);
    },
  };
}
